import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { useCurrentUser, useData } from '../../data/DataContext.tsx';

type PresenceState = { showActive: boolean | null; setShowActive: (value: boolean) => void };

const PresenceContext = createContext<PresenceState>({ showActive: null, setShowActive: () => {} });

// Keeps the signed-in person's "Active now" status while the app is open,
// with their choice to hide it (Settings → Privacy).
export function PresenceProvider({ children }: { children: ReactNode }) {
  const { social } = useData();
  const user = useCurrentUser();
  const uid = user?.uid ?? '';
  const [choice, setChoice] = useState<{ uid: string; showActive: boolean } | null>(null);

  useEffect(() => {
    if (!uid) return;
    let active = true;
    social.showActive().then(showActive => { if (active) setChoice(current => (current?.uid === uid ? current : { uid, showActive })); }, () => undefined);
    return () => { active = false; };
  }, [social, uid]);

  const showActive = choice?.uid === uid ? choice.showActive : null;
  useEffect(() => {
    if (!uid || showActive === null) return;
    return social.trackPresence(showActive);
  }, [social, uid, showActive]);

  return (
    <PresenceContext.Provider value={{ showActive, setShowActive: value => setChoice({ uid, showActive: value }) }}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence(): PresenceState {
  return useContext(PresenceContext);
}
